"use strict";
/**
 * Novig orderbook: subscribe to the server-side WS cache, then poll it.
 *
 * Same contract as the other venues — the server holds the socket, the client
 * polls a pre-bucketed display book (ARCHITECTURE.md). Nothing here opens a
 * WebSocket of its own.
 *
 * NOTE on `ageMs`: for Novig it is 0 while the exchange feed is confirmed
 * live, NOT the time since the book last changed. A quiet pregame market is
 * fresh, and reporting it as stale would train the operator to ignore the
 * indicator (ai/novig/v3-step-5-live-book.md §3).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NOT_READY_RESUBSCRIBE_MS = void 0;
exports.subscribeNovigOrderbook = subscribeNovigOrderbook;
exports.unsubscribeNovigOrderbook = unsubscribeNovigOrderbook;
exports.fetchNovigOrderbook = fetchNovigOrderbook;
exports.startNovigOrderbookPolling = startNovigOrderbookPolling;
const request_1 = require("./api/request");
const novig_1 = require("./api/novig");
/** Give the server a moment to establish the subscription before polling. */
const INITIAL_POLLING_DELAY_MS = 500;
/**
 * Still not ready this long after subscribing: subscribe again. Fast ‹ › taps
 * can land a stale unsubscribe after a fresh subscribe for the same market,
 * and the book is then never built. A repeat subscribe is a no-op server-side.
 */
exports.NOT_READY_RESUBSCRIBE_MS = 3000;
async function subscribeNovigOrderbook(marketIds) {
    await (0, request_1.apiRequest)("POST", "/novig/orderbook/subscribe", {
        body: { marketIds },
    });
}
async function unsubscribeNovigOrderbook(marketIds) {
    try {
        await (0, request_1.apiRequest)("POST", "/novig/orderbook/unsubscribe", {
            body: { marketIds },
        });
    }
    catch (error) {
        // Best-effort: the server's idle sweep drops the subscription anyway,
        // so a failed unsubscribe is not worth surfacing to the operator.
        console.warn("Failed to unsubscribe from Novig orderbook:", error);
    }
}
/**
 * Fetch the pre-bucketed display book.
 *
 * Returns null for "not ready yet" (404: no snapshot or outcome labels yet), so
 * the poll keeps going instead of reporting a failure the operator cannot act
 * on. Anything else throws — a Novig refusal as its operator-facing message
 * (e.g. "Open the Novig app on your phone."), which the order page shows.
 */
async function fetchNovigOrderbook(marketId) {
    try {
        const response = await (0, request_1.apiRequest)("GET", "/novig/orderbook", { params: { marketId, format: "display" } });
        return response?.display ? response : null;
    }
    catch (error) {
        // Not ready yet — or no /novig router at all; either way, nothing to show.
        if (error instanceof request_1.ApiRequestError && error.statusCode === 404)
            return null;
        throw (0, novig_1.translateNovigError)(error);
    }
}
/**
 * Subscribe once, then poll the cached book.
 *
 * The stop function unsubscribes, so the server can close a socket nobody is
 * watching without waiting out its idle timer.
 */
function startNovigOrderbookPolling(marketId, callback, onError, intervalMs = 250) {
    let isPolling = true;
    let timeoutId = null;
    let subscribed = false;
    let subscribedAt = 0;
    async function poll() {
        if (!isPolling)
            return;
        try {
            const response = await fetchNovigOrderbook(marketId);
            // Re-checked after the await: the operator may have navigated away
            // mid-request, and painting then would overwrite the new market.
            if (!isPolling)
                return;
            if (response === null && Date.now() - subscribedAt >= exports.NOT_READY_RESUBSCRIBE_MS) {
                subscribedAt = Date.now();
                subscribeNovigOrderbook([marketId]).catch(() => undefined);
            }
            callback(response);
        }
        catch (error) {
            if (!isPolling)
                return;
            onError(error instanceof Error ? error : new Error(String(error)));
        }
        if (isPolling) {
            timeoutId = window.setTimeout(poll, intervalMs);
        }
    }
    subscribeNovigOrderbook([marketId])
        .then(() => {
        if (!isPolling) {
            void unsubscribeNovigOrderbook([marketId]);
            return;
        }
        subscribed = true;
        subscribedAt = Date.now();
        timeoutId = window.setTimeout(poll, INITIAL_POLLING_DELAY_MS);
    })
        .catch((error) => {
        if (!isPolling)
            return;
        onError(error instanceof Error ? error : new Error(String(error)));
    });
    return () => {
        isPolling = false;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        if (subscribed)
            void unsubscribeNovigOrderbook([marketId]);
    };
}
